import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { isSuperadminOrDeveloper } from '../auth/permissions';
import { getTenantSetupWizardRequirements } from '../api/setupProfilesApi';
import { LoadingSkeleton } from './LoadingSkeleton';
import { StatusBadge } from './StatusBadge';
import {
  formatCapabilityCode,
  formatModuleCode,
  formatSetupProfileCode,
  formatSetupWarning,
} from '../utils/systemLabels';

function ProfileList({ title, items, t }) {
  if (!items?.length) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Stack spacing={1}>
        {items.map((profile) => (
          <Box key={profile.profile_code} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={600}>
              {profile.title_sk
                || formatSetupProfileCode(profile.profile_code)
                || profile.title
                || t(`setupProfiles:${profile.profile_code === 'support_setup' ? 'supportSetup' : profile.profile_code}`, {
                  defaultValue: profile.profile_code,
                })}
            </Typography>
            {profile.required_fields?.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {profile.required_fields.map((field) => {
                  const itemKey = `setupProfiles:items.${field}`;
                  if (i18n.exists(itemKey)) {
                    return t(itemKey);
                  }
                  return formatCapabilityCode(field) || field;
                }).join(' · ')}
              </Typography>
            )}
            {profile.warnings?.length > 0 && (
              <Typography variant="caption" color="warning.main" display="block">
                {profile.warnings.map((warning) => formatSetupWarning(warning) || warning).join(' · ')}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function TenantSetupRequirementsPanel({ tenantId }) {
  const { t } = useTranslation(['setupProfiles', 'setupWizard', 'common']);
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactiveProfiles, setShowInactiveProfiles] = useState(false);

  const canToggleInactive = isSuperadminOrDeveloper(session)
    || process.env.REACT_APP_SHOW_INACTIVE_SETUP_PROFILES === 'true';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = showInactiveProfiles ? { include_inactive: true } : {};
      const response = await getTenantSetupWizardRequirements(tenantId, params);
      setData(response);
    } catch {
      setError(t('setupProfiles:tenant_load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, showInactiveProfiles, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const summary = data?.summary || {};
  const activeModuleCodes = summary.active_module_codes || [];
  const hasModuleSetup = (summary.enabled_module_required?.length || 0) > 0
    || (summary.support?.length || 0) > 0
    || (summary.future_override?.length || 0) > 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">{t('setupWizard:activeModuleSetup')}</Typography>
        <Button component={RouterLink} to="/setup/module-profiles" size="small" variant="outlined">
          {t('setupProfiles:title')}
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>{t('setupProfiles:blueprintNotice')}</Alert>

      {activeModuleCodes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{t('setupWizard:activeModules')}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {activeModuleCodes.map((code) => (
              <StatusBadge
                key={code}
                status={code}
                category="generic"
                apiLabel={formatModuleCode(code) || code}
                size="Sm"
                tone="Brand"
              />
            ))}
          </Stack>
        </Box>
      )}

      {!hasModuleSetup && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('setupWizard:noModuleSpecificSetup')}
        </Alert>
      )}

      {summary.excluded_profiles_count > 0 && !showInactiveProfiles && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('setupWizard:inactiveProfilesHidden', { count: summary.excluded_profiles_count })}
        </Typography>
      )}

      {canToggleInactive && (
        <FormControlLabel
          sx={{ mb: 2 }}
          control={(
            <Switch
              checked={showInactiveProfiles}
              onChange={(event) => setShowInactiveProfiles(event.target.checked)}
            />
          )}
          label={t('setupWizard:showInactiveProfiles')}
        />
      )}

      {showInactiveProfiles && summary.excluded_reason?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{t('setupWizard:excludedProfiles')}</Typography>
          <Stack spacing={0.5}>
            {summary.excluded_reason.map((item) => (
              <Typography key={item.profile_code} variant="caption" color="text.secondary" display="block">
                {formatSetupProfileCode(item.profile_code) || item.title}
                {' '}
                ({formatModuleCode(item.module_code) || item.module_code}): {formatSetupWarning(item.reason) || item.reason}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      <ProfileList title={t('setupWizard:coreSetup')} items={summary.global_required} t={t} />
      <ProfileList title={t('setupProfiles:moduleRequired')} items={summary.enabled_module_required} t={t} />
      <ProfileList title={t('setupProfiles:moduleOptional')} items={summary.optional} t={t} />
      <ProfileList title={t('setupProfiles:futureOverride')} items={summary.future_override} t={t} />
      <ProfileList title={t('setupProfiles:supportSetup')} items={summary.support} t={t} />
    </Box>
  );
}
